import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useTranslations } from "next-intl";
import { useCopyToClipboard } from "usehooks-ts";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Body1B, ButtonText } from "../components/Common/Text";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "../components/ui/dialog";
import {
  isBlockedSite,
  needsLinkConfirmation,
  openSafeLink,
  openWebsite,
  parseWebsiteUrl,
  rememberTrustedSite,
  useLinkSafetyStore,
} from "../utils/link-safety";

// Same phishing feed used by the mobile app. A failed refresh leaves unknown
// sites behind the confirmation instead of treating them as trusted.
const PHISHING_CONFIG_URL =
  "https://cdn.jsdelivr.net/gh/MetaMask/eth-phishing-detect@master/src/config.json";

export const LinkSafetyProvider = (): React.ReactNode => {
  const pendingUrl = useLinkSafetyStore((state) => state.pendingUrl);
  const { data: blockedHosts } = useQuery({
    queryKey: ["link-safety", "phishing-blocklist"],
    queryFn: async ({ signal }) => {
      const { data } = await axios.get(PHISHING_CONFIG_URL, {
        signal,
        timeout: 20000,
      });
      if (!Array.isArray(data?.blacklist))
        throw new Error("Invalid phishing blocklist");
      return data.blacklist
        .filter((host: unknown): host is string => typeof host === "string")
        .map((host: string) => host.toLowerCase());
    },
    staleTime: 60 * 60 * 1000,
    retry: false,
  });

  useEffect(() => {
    if (blockedHosts) useLinkSafetyStore.setState({ blockedHosts });
  }, [blockedHosts]);

  useEffect(() => {
    const onLinkClick = (event: MouseEvent): void => {
      // Run after React handlers so internal party invitations keep their flow.
      if (
        event.defaultPrevented ||
        (event.type === "auxclick" && event.button !== 1)
      )
        return;
      const target = event.target;
      const anchor =
        target instanceof Element ? target.closest("a[href]") : null;
      if (
        !(anchor instanceof HTMLAnchorElement) ||
        anchor.hasAttribute("download")
      )
        return;
      const url = parseWebsiteUrl(anchor.href);
      if (!url) {
        if (/^https?:/i.test(anchor.href)) event.preventDefault();
        return;
      }
      if (!needsLinkConfirmation(url)) return;
      event.preventDefault();
      openSafeLink(url.href);
    };
    document.addEventListener("click", onLinkClick);
    document.addEventListener("auxclick", onLinkClick);
    return () => {
      document.removeEventListener("click", onLinkClick);
      document.removeEventListener("auxclick", onLinkClick);
    };
  }, []);

  return pendingUrl ? (
    <LinkConfirmation key={pendingUrl} url={pendingUrl} />
  ) : null;
};

const LinkConfirmation = ({ url }: { url: string }): React.ReactNode => {
  const t = useTranslations();
  const [alwaysTrust, setAlwaysTrust] = useState(false);
  const [copied, setCopied] = useState(false);
  const [, copy] = useCopyToClipboard();
  const blockedHosts = useLinkSafetyStore((state) => state.blockedHosts);
  const hostname = parseWebsiteUrl(url)?.hostname ?? "";
  const isBlacklisted = blockedHosts.includes(hostname);
  const close = (): void => {
    useLinkSafetyStore.setState({ pendingUrl: null });
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogContent
        zIndexBase={200}
        className="link-safety-bottom-sheet grid-cols-1 max-sm:top-auto max-sm:bottom-0 max-sm:translate-y-0 max-sm:rounded-b-none max-h-[90dvh] overflow-y-auto px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] gap-6"
      >
        <DialogTitle
          className={
            isBlacklisted
              ? "app-text-h5 font-bold leading-7 tracking-normal text-red-delete text-center pr-10"
              : "app-text-h5 font-bold leading-7 tracking-normal text-center pr-10"
          }
        >
          {t(isBlacklisted ? "security_warning" : "this_link_may_be_unsafe")}
        </DialogTitle>
        <DialogDescription className="app-text-body2-medium text-center text-subtle-light">
          {t(
            isBlacklisted
              ? "this_link_is_blacklisted_desc"
              : "untrusted_website_desc"
          )}
        </DialogDescription>
        <Body1B className="min-w-0 text-center break-words [overflow-wrap:anywhere]">
          {url}
        </Body1B>
        {!isBlacklisted && (
          <label className="flex items-center gap-3 cursor-pointer text-subtle-light">
            <Checkbox
              checked={alwaysTrust}
              onCheckedChange={(checked) => {
                setAlwaysTrust(checked === true);
              }}
            />
            <span className="app-text-body2-medium">
              {t("always_trust_this_site")}
            </span>
          </label>
        )}
        <div className="grid grid-cols-2 gap-4">
          <Button
            variant="outline"
            onClick={() => {
              void copy(url)
                .then((success) => {
                  if (success) setCopied(true);
                })
                .catch(() => {});
            }}
          >
            <ButtonText>
              {t(copied ? "link_safety_copied" : "link_safety_copy")}
            </ButtonText>
          </Button>
          <Button
            variant={isBlacklisted || !alwaysTrust ? "destructive" : "default"}
            onClick={() => {
              // Recheck the latest blocklist before saving trust.
              if (alwaysTrust && !isBlockedSite(hostname))
                rememberTrustedSite(url);
              openWebsite(url);
              close();
            }}
          >
            <ButtonText>
              {t(
                isBlacklisted
                  ? "link_safety_continue"
                  : alwaysTrust
                    ? "open_link"
                    : "trust"
              )}
            </ButtonText>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
